import React, { useMemo, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn, filterUsers } from '../lib/utils';
import { selectableUsers, userLabel } from '../lib/userPicker';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandInput, CommandItem, CommandList } from './ui/command';

const UserCombobox = ({ users, value, onChange, placeholder }) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  // Sorting runs Intl collation across every user. Keeping it out of the query
  // path means it happens once per user-list change rather than once per
  // keystroke.
  const eligible = useMemo(() => selectableUsers(users), [users]);
  const matches = useMemo(() => filterUsers(eligible, query), [eligible, query]);

  const selected = eligible.find((user) => user.id === value);

  const handleSelect = (user) => {
    setOpen(false);
    setQuery('');
    onChange(user.id);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Reopening should start from the full list, not from whatever the
        // operator typed before picking someone last time.
        if (!next) setQuery('');
      }}
      // Required. PopoverContent portals to document.body, outside the parent
      // Dialog's focus trap; without `modal` the Dialog pulls focus straight
      // back and the search input cannot be typed into at all.
      modal
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid="select-user"
          className="w-full justify-between px-3 font-normal"
        >
          <span className={cn('truncate', !selected && 'text-muted-foreground')}>
            {selected ? userLabel(selected) : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        // p-0 overrides PopoverContent's default p-4; the width binding
        // overrides its default w-72, which would leave the list narrower than
        // its own trigger. The explicit var() form matches select.jsx:65 and
        // navigation-menu.jsx:69 — the bare-variable shorthand has no
        // precedent in this codebase and a silently dropped class here is hard
        // to trace back to a Tailwind arbitrary-value form.
        className="w-[var(--radix-popover-trigger-width)] p-0"
      >
        {/* shouldFilter={false}: cmdk's built-in filter keys off each item's
            `value`, which would either break selection (when `value` is the
            user id) or match ambiguously for two users sharing a name.
            Filtering explicitly reuses the same tested helper the Users page
            uses, so both search boxes behave identically. */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t('search')}
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {/* Deliberately not CommandEmpty: with shouldFilter={false} its
                count is cmdk's registered-item tally, maintained by mount and
                unmount effects, so it trails this render by a tick. A plain
                conditional on `matches` is synchronous with the list it
                describes and cannot disagree with it. */}
            {matches.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {t('no_users')}
              </div>
            ) : (
              matches.map((user) => (
                <CommandItem
                  key={user.id}
                  // cmdk only uses this for item identity and keyboard
                  // navigation; ids are unique so that is all it needs. The
                  // handler closes over `user` rather than reading cmdk's
                  // argument, so the rendered row and the selected record can
                  // never drift apart.
                  value={user.id}
                  onSelect={() => handleSelect(user)}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      user.id === value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="truncate">{userLabel(user)}</span>
                  {!user.is_active && (
                    <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                      {t('inactive')}
                    </span>
                  )}
                </CommandItem>
              ))
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default UserCombobox;
